import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card, Icon, Title, TopSection } from "./Card.Style";
import { buttonCSS } from "../general.style";
import { postAnalytics } from "../../../../../PatientComponents/analytics";
import InstructionsSteps from "./InstructionsSteps";
import { usePatientFacing } from "../../../../flow/patientFacing/hooks/usePatientFacing";

const nutrition = "/Fasting instructions.svg";
const INSTRUCTIONS_LANG_SUFFIX = { en: "", es: "_ES", pt: "_PT" };

const normalizeInstructionLanguage = (language) => {
  const normalized = (language || "").toLowerCase();
  if (normalized.startsWith("pt")) return "pt";
  if (normalized.startsWith("es") || normalized.startsWith("sp")) return "es";
  return "en";
};

const buildInstructionsPdf = (doctorName, concentrate, language) => {
  if (!doctorName || !concentrate) return null;

  const cleanDoctorName = doctorName.replace(/^dr\.?\s*/i, "").trim();
  const languageKey = normalizeInstructionLanguage(language);
  const fileName = `UGI_${cleanDoctorName}_${concentrate}${INSTRUCTIONS_LANG_SUFFIX[languageKey]}.pdf`;

  return {
    fileName,
    url: `/docs/instructions/${languageKey.toUpperCase()}/${encodeURIComponent(fileName)}`,
  };
};

// Parse date string to Date object safely
const parseDate = (dateInput) => {
  if (!dateInput) return null;
  if (typeof dateInput === 'string') {
    // Extract date part before 'T' if present, then force local timezone
    const datePart = dateInput.includes('T') ? dateInput.split('T')[0] : dateInput;
    return new Date(`${datePart}T00:00:00`);
  }
  return new Date(dateInput);
};

export default function NutritionalGuidelines({ keyPrefix = "", showDownloadButton = false }) {
  const { t, i18n } = useTranslation();
  const event = usePatientFacing().getEventDataLocal();

  const { Case } = event || {};

  const { id: caseId } = Case || {};
  const { procedureDate, doctorName, concentrate, instructionProtocol } = Case?.GiCase || {};
  // A clinic-specific protocol must not link to a PDF containing different instructions.
  const instructionsPdf = instructionProtocol ? null : buildInstructionsPdf(doctorName, concentrate, i18n.language);
  const [isPdfAvailable, setIsPdfAvailable] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function checkPdfAvailability() {
      if (!instructionsPdf?.url) {
        if (isMounted) setIsPdfAvailable(false);
        return;
      }

      try {
        const response = await fetch(instructionsPdf.url, {
          method: "HEAD",
          cache: "no-store",
        });
        // The SPA fallback serves index.html with a 200 status for missing
        // files, so a 200 alone doesn't prove the PDF exists.
        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        if (isMounted) setIsPdfAvailable(response.ok && contentType.includes("application/pdf"));
      } catch (error) {
        if (isMounted) setIsPdfAvailable(false);
      }
    }

    setIsPdfAvailable(null);
    checkPdfAvailability();

    return () => {
      isMounted = false;
    };
  }, [instructionsPdf?.url]);

  const handlePersonalNutritionClick = () => {
    postAnalytics({ userId: caseId, type: "nutrition-instruction-clicked" });
  };

  return (
    <Card>
      <TopSection>
        <Title>
          {t(`${keyPrefix}Nutritional-Instructions-Title`)}
        </Title>
        <Icon src={nutrition} alt="nutrition" />
      </TopSection>

      <Text>
        {t(`${keyPrefix}Nutritional-Instructions-Preview`)}
      </Text>
      {!keyPrefix?.includes("GI2") && (
        <Text>
          {t(`${keyPrefix}Nutritional-Instructions-scheduled`)}{" "}
          {(() => {
            const parsedDate = parseDate(procedureDate);
            if (!parsedDate) return "";
            return parsedDate.toLocaleDateString(i18n.language || "en", {
              weekday: "long",
              year: "numeric",
              month: "numeric",
              day: "numeric",
            });
          })()}
        </Text>
      )}

      <InstructionsSteps keyPrefix={keyPrefix} />

      {showDownloadButton && instructionsPdf && isPdfAvailable === true && (
        <StyledButton
          href={instructionsPdf.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handlePersonalNutritionClick}
        >
          {t(`${keyPrefix}Nutritional-Instructions-Download`)}
        </StyledButton>
      )}
    </Card>
  );
}

const Text = styled.p`
  margin: 0;
  margin-block-end: 1.125rem;
  font-size: 1.188rem;
`;

const StyledButton = styled.a`
  margin-block-start: 1.125rem;
  text-align: center;
  ${buttonCSS}
`;
